<template>
  <CardProjectActivityBase
    title="Target vs Achievement"
    v-model:projectId="projectId"
    :loadingData="loading"
    :hasData="tvaData.length > 0"
    loadingText="Loading report data..."
    @selected="fetchTvaData"
    @print="printReport"
  >
    <div class="table-responsive scrollable-table">
      <table class="table table-sm table-bordered table-striped mb-0">
        <thead>
          <tr class="text-dark bg-white">
            <th class="text-left py-1">Activity Group</th>
            <th class="text-right py-1">Target</th>
            <th class="text-right py-1">Already Achieved</th>
            <th class="text-right py-1">Balance</th>
            <th class="text-right py-1">Achievement %</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in tvaData" :key="item.id" class="align-middle">
            <td :class="['activity-name', 'indent-level-' + item.level, item.level < 1 ? 'font-bold' : '']">
              {{ item.name }}
            </td>
            <td class="text-right">{{ commaFormat(item.total_target) }}</td>
            <td class="text-right">{{ commaFormat(item.total_achievement) }}</td>
            <td class="text-right">{{ commaFormat(item.total_target - item.total_achievement) }}</td>
            <td class="text-right font-bold">{{ calculatePercent(item) }}%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="text-right mt-1 pb-1">
      <a @click="seeDetails" class="text-primary font-bold cursor-pointer small">
        See Details.. <i class="feather icon-arrow-right"></i>
      </a>
    </div>
  </CardProjectActivityBase>
</template>

<script setup>
import { ref, inject } from 'vue'
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router'
import CardProjectActivityBase from "./CardProjectActivityBase.vue";
import handleProjects from "@/services/modules/procurement/project";
import { useSharedProjectList } from "@/services/modules/procurement/useSharedProjectList";
import { commaFormat } from "@/services/utils/global";
import useDate from "@/services/utils/day";
import dashboardCardPdfPrinter from "@/services/utils/dashboardCardPdfPrinter";

const { fetchProjectActivityGroupTva } = handleProjects();
const { projects } = useSharedProjectList();
const { firstDateOfMonth, lastDateOfMonth } = useDate();

const route = useRoute();
const router = useRouter();
const store = useStore();
const showError = inject('showError');
const { printCardReport } = dashboardCardPdfPrinter();

const projectId = ref(null);
const tvaData = ref([]);
const loading = ref(false);
const pageId = ref(null);

const props = defineProps({
  page: Object,
  itemKey: String,
  statisticsItem: Object,
  timeRange: Array
})

const fetchTvaData = async () => {
  if (!projectId.value) return;
  
  loading.value = true;
  try {
    const query = `?company_id=${route.params.companyId}&start_date=${firstDateOfMonth}&end_date=${lastDateOfMonth}`;
    const res = await fetchProjectActivityGroupTva(projectId.value, query);
    if (!res.status) {
      tvaData.value = [];
      showError(res.message || 'Failed to fetch data');
      return;
    }

    tvaData.value = res.data;
    if (res.page_id) {
      pageId.value = res.page_id;
    }
  } catch (error) {
    showError('Something went wrong');
  } finally {
    loading.value = false;
  }
};

const calculatePercent = (item) => {
  if (!item.total_target || item.total_target <= 0) return 0;
  return ((item.total_achievement / item.total_target) * 100).toFixed(2);
};

const seeDetails = () => {
  const routeData = router.resolve({
    name: 'narrative-report',
    params: { 
      ...route.params, 
      pageId: pageId.value || (props.page ? props.page.id : null)
    }
  });
  window.open(routeData.href, '_blank');
};

const printReport = () => {
  if (!projectId.value) {
    showError('Please select a project first');
    return;
  }
  
  const selectedProject = projects.value.find(p => p.id === projectId.value);
  const company = store.getters['company/getCompany'];
  
  const headers = [
    { text: 'Activity Group', width: '*' },
    { text: 'Target', width: 'auto', alignment: 'right' },
    { text: 'Already Achieved', width: 'auto', alignment: 'right' },
    { text: 'Balance', width: 'auto', alignment: 'right' },
    { text: 'Achievement %', width: 'auto', alignment: 'right' }
  ];
  
  const bodyData = tvaData.value.map(item => [
    { text: item.name, margin: [item.level * 15, 0, 0, 0], bold: item.level < 1 },
    { text: commaFormat(item.total_target), alignment: 'right' },
    { text: commaFormat(item.total_achievement), alignment: 'right' },
    { text: commaFormat(item.total_target - item.total_achievement), alignment: 'right' },
    { text: `${calculatePercent(item)}%`, alignment: 'right', bold: true }
  ]);
  
  printCardReport(
    'Target vs Achievement Report',
    headers,
    bodyData,
    company,
    `Project: ${selectedProject ? selectedProject.name : ''}`
  );
};
</script>

<style scoped>
.scrollable-table {
  max-height: 450px;
  overflow-y: auto;
}
.activity-name {
  text-align: left;
}
.indent-level-0 { padding-left: 5px !important; }
.indent-level-1 { padding-left: 25px !important; }
.indent-level-2 { padding-left: 45px !important; }
.indent-level-3 { padding-left: 65px !important; }
.indent-level-4 { padding-left: 85px !important; }

.font-bold {
  font-weight: 600;
}
.cursor-pointer {
  cursor: pointer;
}
.text-primary {
  color: #7367f0 !important;
}
</style>
